// src/components/pages/mypage/hooks/useMyPosts.js

import { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_API_BASE_URL;

export default function useMyPosts(auth, handleConfirm, currentUserId, t) {
  const [myPostsTab, setMyPostsTab] = useState('feed');
  const [feedPosts, setFeedPosts] = useState([]);
  const [loadingFeedPosts, setLoadingFeedPosts] = useState(true);
  const [myPosts, setMyPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [savedPosts, setSavedPosts] = useState([]);
  const [loadingSavedPosts, setLoadingSavedPosts] = useState(true);

  const postsUrl = `${API_URL}/posts?userId=${currentUserId}`;
  const savedPostsUrl = `${API_URL}/user/saved-posts/me`;

  // 1. 피드 포스트 로드
  useEffect(() => {
    if (!auth.isAuthenticated) return;
    setLoadingFeedPosts(true);
    fetch(`${API_URL}/posts/feed/me`, { headers: { Authorization: `Bearer ${auth.user?.id_token}` } })
      .then(res => res.json().then(data => (res.ok ? data : Promise.reject(data))))
      .then(data => setFeedPosts(data.items || []))
      .catch(err => {
        console.error('Failed to load feed posts', err);
        setFeedPosts([]);
      })
      .finally(() => setLoadingFeedPosts(false));
  }, [auth.user?.id_token, auth.isAuthenticated]);

  // 2. 내가 쓴 포스트 로드
  useEffect(() => {
    if (!auth.isAuthenticated || !currentUserId) return;
    setLoadingPosts(true);
    fetch(postsUrl, { headers: { Authorization: `Bearer ${auth.user?.id_token}` } })
      .then(res => res.json().then(data => (res.ok ? data : Promise.reject(data))))
      .then(data => setMyPosts(Array.isArray(data.items) ? data.items : []))
      .catch(err => {
        console.error('Failed to load posts', err);
        setMyPosts([]);
      })
      .finally(() => setLoadingPosts(false));
  }, [postsUrl, auth.user?.id_token, auth.isAuthenticated, currentUserId]);

  // 3. 저장된 포스트 로드
  useEffect(() => {
    if (!auth.isAuthenticated) return;
    setLoadingSavedPosts(true);
    fetch(savedPostsUrl, { headers: { Authorization: `Bearer ${auth.user?.id_token}` } })
      .then(res => res.json().then(data => (res.ok ? data : Promise.reject(data))))
      .then(data => setSavedPosts(data.savedPosts || []))
      .catch(err => {
        console.error('Failed to load saved posts', err);
        setSavedPosts([]);
      })
      .finally(() => setLoadingSavedPosts(false));
  }, [savedPostsUrl, auth.user?.id_token, auth.isAuthenticated]);

  // 포스트 삭제
  const handleDeletePost = useCallback(async (postId) => {
    handleConfirm({
      message: t('mypage.confirm.deletePost'),
      callback: async () => {
        try {
          const res = await fetch(`${API_URL}/posts/${postId}`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${auth.user.id_token}` },
          });
          if (!res.ok) throw new Error(`Failed to delete post (${res.status})`);
          setMyPosts(prev => prev.filter(p => p.postId !== postId));
          alert(t('mypage.alert.postDeleted'));
        } catch (err) {
          console.error('Failed to delete post', err);
          alert(`${t('mypage.error.deletePost')} ${err.message}`);
        }
      }
    });
  }, [auth.user?.id_token, handleConfirm, t]);

  // 포스트 저장 취소 (Unsave)
  const handleUnsavePost = useCallback(async (postId) => {
    handleConfirm({
      message: t('mypage.confirm.unsavePost'),
      callback: async () => {
        try {
          const res = await fetch(`${API_URL}/posts/${postId}/save`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${auth.user.id_token}` },
          });
          if (!res.ok) throw new Error(`Failed to unsave post (${res.status})`);
          setSavedPosts(prev => prev.filter(p => p.postId !== postId));
          alert(t('mypage.alert.postUnsaved'));
        } catch (err) {
          console.error('Failed to unsave post', err);
          alert(t('mypage.error.unsavePostGeneric') + (err.message || ''));
        }
      }
    });
  }, [auth.user?.id_token, handleConfirm, t]);


  return {
    myPostsTab,
    setMyPostsTab,
    feedPosts,
    loadingFeedPosts,
    myPosts,
    loadingPosts,
    savedPosts,
    loadingSavedPosts,
    handleDeletePost,
    handleUnsavePost,
  };
}